import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  CheckCircle2, 
  HeadphonesIcon, 
  TestTube2, 
  BarChart3, 
  Share2,
  DollarSign,
  Clock,
  Zap,
  Award,
  Users,
  TrendingUp,
  Mail,
  Calendar,
  ArrowRight,
  Shield,
  Package,
  Warehouse,
  ClipboardList,
  Receipt
} from 'lucide-react';
import { trustMetrics, services, benefits, companyInfo, sapModules } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ name: '', email: '', company: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Sorry, there was an error submitting your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScheduleConsultation = () => {
    window.location.href = `mailto:${companyInfo.email}?subject=Schedule Consultation Request&body=Hi, I would like to schedule a consultation about SAP services.`;
  };

  const serviceIcons = {
    'SAP Support': HeadphonesIcon,
    'SAP Testing': TestTube2,
    'SAP Business Analysis': BarChart3,
    'SAP Integration Services': Share2
  };

  const benefitIcons = [
    DollarSign,
    Clock,
    Zap,
    Award,
    Users,
    TrendingUp
  ];

  const moduleIcons = {
    MM: Package,
    WM: Warehouse,
    IM: ClipboardList,
    AP: Receipt
  };

  // Update nav links to include #modules

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img src="/logo.svg" alt="Ramsporia IT Services" className="h-14 w-auto" data-testid="header-logo" />
            </div>
            <nav className="hidden md:flex space-x-6 lg:space-x-8">
              <a href="#services" data-testid="nav-services" className="text-gray-700 hover:text-blue-900 transition-colors font-medium">Services</a>
              <a href="#modules" data-testid="nav-modules" className="text-gray-700 hover:text-blue-900 transition-colors font-medium">SAP Modules</a>
              <a href="#why-us" data-testid="nav-why-us" className="text-gray-700 hover:text-blue-900 transition-colors font-medium">Why Us</a>
              <a href="#about" data-testid="nav-about" className="text-gray-700 hover:text-blue-900 transition-colors font-medium">About</a>
              <a href="#contact" data-testid="nav-contact" className="text-gray-700 hover:text-blue-900 transition-colors font-medium">Contact</a>
            </nav>
            <Button 
              data-testid="header-get-started-btn"
              onClick={() => scrollToSection('contact')}
              className="bg-blue-900 hover:bg-blue-800 text-white transition-colors"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-8">
            <div className="inline-block">
              <div className="flex items-center justify-center space-x-2 bg-blue-50 px-4 py-2 rounded-full border border-blue-100">
                <Shield className="h-4 w-4 text-blue-900" />
                <span className="text-sm font-medium text-blue-900">Trusted SAP Partner</span>
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Reliable SAP Offshore Support &<br />
              <span className="text-blue-900">Integration Services</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We help global businesses reduce SAP operational costs with efficient offshore support, 
              SAP testing, business analysis, and integration services from India.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Button 
                data-testid="hero-book-consultation-btn"
                size="lg" 
                onClick={() => scrollToSection('contact')}
                className="bg-blue-900 hover:bg-blue-800 text-white px-8 py-6 text-lg transition-all hover:scale-105"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book Free Consultation
              </Button>
              <Button 
                data-testid="hero-contact-us-btn"
                size="lg" 
                variant="outline" 
                onClick={() => scrollToSection('contact')}
                className="border-2 border-blue-900 text-blue-900 hover:bg-blue-50 px-8 py-6 text-lg transition-all hover:scale-105"
              >
                Contact Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {trustMetrics.map((metric) => (
              <div key={metric.id} className="text-center space-y-2">
                <div className="text-3xl sm:text-4xl font-bold text-white">{metric.value}</div>
                <div className="text-blue-200 text-sm sm:text-base">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive SAP solutions tailored to your business needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => {
              const IconComponent = serviceIcons[service.title];
              return (
                <Card 
                  key={service.id} 
                  className="border-2 border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300 group"
                >
                  <CardHeader>
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                        <IconComponent className="h-6 w-6 text-blue-900" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-xl text-gray-900">{service.title}</CardTitle>
                        <CardDescription className="mt-2 text-gray-600">
                          {service.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center space-x-3">
                          <CheckCircle2 className="h-5 w-5 text-blue-900 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* SAP Module Expertise Section */}
      <section id="modules" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 via-white to-slate-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <div className="inline-block">
              <div className="flex items-center justify-center space-x-2 bg-blue-50 px-4 py-2 rounded-full border border-blue-100">
                <Shield className="h-4 w-4 text-blue-900" />
                <span className="text-sm font-medium text-blue-900">Functional Expertise</span>
              </div>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">SAP Module Expertise</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Deep functional knowledge across core SAP modules powering global supply chains and finance.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {sapModules.map((module) => {
              const ModuleIcon = moduleIcons[module.code];
              return (
                <Card
                  key={module.id}
                  data-testid={`module-card-${module.code.toLowerCase()}`}
                  className="border-2 border-gray-100 hover:border-blue-900 hover:shadow-xl transition-all duration-300 bg-white group relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-blue-900/5 rounded-bl-full group-hover:bg-blue-900/10 transition-colors" />
                  <CardHeader className="relative">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-3 bg-blue-900 rounded-lg group-hover:scale-110 transition-transform">
                        <ModuleIcon className="h-6 w-6 text-white" />
                      </div>
                      <span className="text-3xl font-extrabold text-blue-900/20 group-hover:text-blue-900/40 transition-colors tracking-tighter">
                        {module.code}
                      </span>
                    </div>
                    <CardTitle className="text-lg text-gray-900 leading-snug">{module.name}</CardTitle>
                    <CardDescription className="text-gray-600 text-sm leading-relaxed">
                      {module.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="relative">
                    <ul className="space-y-2 border-t border-gray-100 pt-4">
                      {module.capabilities.map((cap, idx) => (
                        <li key={idx} className="flex items-center space-x-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-blue-900 flex-shrink-0" />
                          <span className="text-gray-700">{cap}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Looking for support on a specific SAP module?</p>
            <Button
              data-testid="modules-discuss-btn"
              size="lg"
              onClick={() => scrollToSection('contact')}
              className="bg-blue-900 hover:bg-blue-800 text-white transition-all hover:scale-105"
            >
              Discuss Your Requirements
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>


      {/* Why Choose Us Section */}
      <section id="why-us" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Why Choose Us</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Experience the advantage of working with a dedicated offshore SAP partner
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const IconComponent = benefitIcons[index];
              return (
                <Card 
                  key={benefit.id} 
                  className="border border-gray-200 hover:shadow-lg transition-all duration-300 bg-white group"
                >
                  <CardHeader>
                    <div className="p-3 bg-blue-50 rounded-lg w-fit group-hover:bg-blue-100 transition-colors">
                      <IconComponent className="h-6 w-6 text-blue-900" />
                    </div>
                    <CardTitle className="text-lg text-gray-900 mt-4">{benefit.title}</CardTitle>
                    <CardDescription className="text-gray-600">
                      {benefit.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">About Our Company</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {companyInfo.description}
            </p>
            <div className="pt-6">
              <Button 
                data-testid="about-learn-more-btn"
                size="lg" 
                onClick={() => scrollToSection('contact')}
                className="bg-blue-900 hover:bg-blue-800 text-white transition-colors"
              >
                Learn More About Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Get In Touch</h2>
            <p className="text-xl text-gray-600">
              Ready to optimize your SAP operations? Let's talk.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-gray-200">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-4">
                  <Mail className="h-6 w-6 text-blue-900 mt-1" />
                  <div>
                    <div className="font-semibold text-gray-900">Email</div>
                    <a 
                      href={`mailto:${companyInfo.email}`} 
                      className="text-blue-900 hover:underline"
                    >
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
                <div className="pt-4 space-y-3">
                  <Button 
                    data-testid="send-email-btn"
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white transition-colors"
                    onClick={() => window.location.href = `mailto:${companyInfo.email}`}
                  >
                    <Mail className="mr-2 h-5 w-5" />
                    Send Email
                  </Button>
                  <Button 
                    data-testid="schedule-consultation-btn"
                    variant="outline" 
                    onClick={handleScheduleConsultation}
                    className="w-full border-2 border-blue-900 text-blue-900 hover:bg-blue-50 transition-colors"
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Schedule Consultation
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900">Send Us a Message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div>
                    <Input 
                      data-testid="contact-name-input"
                      placeholder="Your Name" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <Input 
                      data-testid="contact-email-input"
                      type="email" 
                      placeholder="Your Email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <Input 
                      data-testid="contact-company-input"
                      placeholder="Company Name" 
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <Textarea 
                      data-testid="contact-message-input"
                      placeholder="Your Message" 
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="border-gray-300"
                    />
                  </div>
                  <Button 
                    data-testid="contact-submit-btn"
                    type="submit" 
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white transition-colors"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="bg-white rounded-lg p-2 inline-block">
                  <img src="/logo.svg" alt="Ramsporia IT Services" className="h-10 w-auto" />
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                Your trusted partner for SAP offshore support and integration services.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#services" className="hover:text-white transition-colors">Services</a></li>
                <li><a href="#modules" className="hover:text-white transition-colors">SAP Modules</a></li>
                <li><a href="#why-us" className="hover:text-white transition-colors">Why Choose Us</a></li>
                <li><a href="#about" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>
                  <a href={`mailto:${companyInfo.email}`} className="hover:text-white transition-colors">
                    {companyInfo.email}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>© {companyInfo.year} {companyInfo.name}. All rights reserved.</p>
            <p className="mt-2 text-xs">
              SEO: SAP MM | SAP WM | SAP IM | SAP AP | SAP Offshore Support | SAP AMS Services | SAP Testing Services | SAP Business Analyst | 
              SAP Integration Support | SAP India Offshore Team | SAP Managed Services
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;